import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from 'react-toastify';


export default function Signin() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [sessionData, setSessionData] = useState({
    status: "active",
    platform: "web",
    deviceName: "Unknown",
    locationIp: "",
  });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSendingResetLink, setIsSendingResetLink] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      navigate("/token/rzto");
    } else {
      createUserSessionData();
    }
  }, [navigate]);

  useEffect(() => {
    createUserSessionData();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.email) newErrors.email = "Email is required";
    if (!showForgotPassword && !formData.password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoggingIn(true);

    try {
      const baseUrl = process.env.REACT_APP_API;
      console.log("BaseURL",baseUrl);
      if (!baseUrl) throw new Error("API URL is not configured!");

      const response = await fetch(`${baseUrl}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          ipAddress: sessionData.locationIp,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || "Invalid login credentials.");

      sessionStorage.setItem("authToken", data.token);
      toast.success("Logged in successful!");
      navigate("/token/rzto");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error("Please enter your email address");
      return;
    }

    setIsSendingResetLink(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success(`Password reset link sent to ${formData.email}`);
      setShowForgotPassword(false);
    } catch (error) {
      toast.error("Failed to send reset link. Please try again.");
    } finally {
      setIsSendingResetLink(false);
    }
  };

  const toggleForgotPassword = () => {
    setShowForgotPassword(!showForgotPassword);
    setErrors({});
  };

  async function createUserSessionData() {
    setSessionData({
      status: "active",
      platform: isAppEnvironment() ? "app" : "web",
      deviceName: getDeviceName(),
      locationIp: await getIPAddress(),
    });
  }

  function isAppEnvironment() {
    return typeof window.MyAppBridge !== "undefined";
  }

  function getDeviceName() {
    const matches = navigator.userAgent.match(/\(([^)]+)\)/);
    return matches ? matches[1] : "Unknown";
  }

  async function getIPAddress() {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error("Error fetching IP address:", error);
      return "Unavailable";
    }
  }

  return (
    <>
      <div
        style={{
          backgroundImage: `url(/images/main-background.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "100vh",
          width: "100%",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: -1,
          filter: "blur(10px)",
        }}
      ></div>
      <div className="bg-overlay"></div>

      <div className="auth-container" style={{ marginTop: '80px', marginBottom:'80px' }}>
        <div className="auth-wrapper">
          <div className="auth-card">
            {showForgotPassword ? (
              <>
                <h1 className="auth-title">Reset Your Password</h1>
                <p className="auth-subtitle">
                  Enter your email to receive a reset link
                </p>

                <form onSubmit={handleForgotPassword} className="auth-form">
                  <div className="form-group">
                    <div className="input-with-icon">
                      <Icon icon="mage:email" className="input-icon" />
                      <input
                        type="email"
                        name="email"
                        className="form-input"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.email && <p className="error-message">{errors.email}</p>}
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={isSendingResetLink}
                  >
                    {isSendingResetLink ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Sending...
                      </>
                    ) : (
                      "Send Reset Link"
                    )}
                  </button>

                  <div className="back-to-login">
                    <button
                      type="button"
                      className="forgot-password-btn"
                      onClick={toggleForgotPassword}
                    >
                      Back to Login
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h1 className="auth-title">Sign In to Your Account</h1>
                <p className="auth-subtitle">
                  Welcome back! Please enter your details.
                </p>

                <form onSubmit={handleLogin} className="auth-form">
                  <div className="form-group">
                    <div className="input-with-icon">
                      <Icon icon="mage:email" className="input-icon" />
                      <input
                        type="email"
                        name="email"
                        className="form-input"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.email && <p className="error-message">{errors.email}</p>}
                  </div>

                  <div className="form-group">
                    <div className="input-with-icon password-container">
                      <Icon icon="solar:lock-password-outline" className="input-icon" />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className="form-input"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <span
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <Icon icon={showPassword ? "mdi:eye-off" : "mdi:eye"} />
                      </span>
                    </div>
                    {errors.password && <p className="error-message">{errors.password}</p>}
                  </div>

                  <div className="forgot-password">
                  <Link to="/forgot-password" className="forgot-password-btn">
                  Forgot Password?
                </Link>
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={isLoggingIn}
                  >
                    {isLoggingIn ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Signing In...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </button>
                </form>

                <div className="auth-redirect">
                  <p>
                    Don't have an account?{" "}
                    <Link to="/token/signup" className="redirect-link">
                      Sign Up
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .auth-container {
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          box-sizing: border-box;
        }
        
        .auth-wrapper {
          width: 100%;
          max-width: 500px;
        }
        
        .auth-card {
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .auth-title {
          font-size: 2rem;
          color: #333;
          text-align: center;
          margin-bottom: 10px;
        }
        
        .auth-subtitle {
          color: #666;
          text-align: center;
          margin-bottom: 30px;
        }
        
        .form-group {
          margin-bottom: 20px;
        }
        
        .input-with-icon {
          position: relative;
        }
        
        .input-icon {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: #666;
          font-size: 1.2rem;
        }
        
        .form-input {
          width: 100%;
          padding: 15px 15px 15px 45px;
          border-radius: 8px;
          border: 1px solid #ddd;
          font-size: 1rem;
          box-sizing: border-box;
        }
        
        .password-container {
          position: relative;
        }
        
        .password-toggle {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          color: #666;
        }
        
        .forgot-password, .back-to-login {
          text-align: center;
          margin: 20px 0;
        }
        
        .forgot-password-btn {
          background: none;
          border: none;
          color: #f0b90b;
          cursor: pointer;
          font-size: 0.9rem;
          padding: 0;
        }
        
        .forgot-password-btn:hover {
          text-decoration: underline;
        }
        
        .submit-button {
          width: 100%;
          padding: 15px;
          border-radius: 8px;
          border: none;
          background-color: #f0b90b;
          color: white;
          font-size: 1rem;
          cursor: pointer;
          transition: background-color 0.3s;
        }
        
        .submit-button:hover {
          background-color: #e0a800;
        }
        
        .submit-button:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }
        
        .auth-redirect {
          text-align: center;
          margin-top: 20px;
          color: #666;
        }
        
        .redirect-link {
          color: #f0b90b;
          text-decoration: none;
          font-weight: 600;
        }
        
        .redirect-link:hover {
          text-decoration: underline;
        }
        
        .error-message {
          color: #dc3545;
          font-size: 0.8rem;
          margin-top: 5px;
        }
        
        @media (max-width: 768px) {
          .auth-card {
            padding: 20px;
          }
          
          .input-icon {
            display:none;
          }
          
          .auth-title {
            font-size: 1.5rem;
          }
          
          .form-input {
            padding: 12px 12px 12px 40px;
          }
        }
        
        @media (max-width: 480px) {
          .auth-container {
            padding: 10px;
            margin-top: 60px;
          }

          .input-icon {
            display:none;
          }
          
          .auth-card {
            padding: 15px;
          }
          
          .auth-title {
            font-size: 1.3rem;
          }
          
          .auth-subtitle {
            font-size: 0.9rem;
          }
        }
      `}</style>
    </>
  );
}